import React, { ReactNode, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAlerts } from "../../contexts/AlertsContext";
import APIEndPoints from "../../utils/APIEndPoints";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import { IApiClientError } from "../../shared/ApiClient";
import APIError from "../../utils/APIError";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import Loading from "../common/Loading";
import FormContainer from "../common/FormContainer";
import { FaInfoCircle } from "react-icons/fa";
import FormInputText from "../common/FormInputText";
import FormTextArea from "../common/FormTextArea";
import FormInputImage from "../common/FormInputImage";
import FormDropDown from "../common/FormDropDown";
import UploadManager from "../../shared/UploadManager";
import UploadFileType from "../../model/enums/UploadFileType";
import ImgUtil from "../../utils/ImgUtil";
import _3DModelLicenseType from "../../model/enums/_3DModelLicenseType";
import FormInputFile from "../common/FormInputFile";
import AppSubSections from "../../utils/AppSubSections";
import Texture from "../../model/Texture";
import TextureType from "../../model/enums/TextureType";
import FormDownloadFile from "../common/FormDownloadFile";
import UploadManagerProgressReport from "../../model/UploadManagerProgressReport";
import UploadFileStatus from "../../model/enums/UploadFileStatus";
import { EXPLANATION_HEADER } from "../common/dialog_window/LicenseDialog";
import TextureFileType from "../../model/enums/TextureFileType";
import TextureFile from "../../model/TextureFile";
import PagedData from "../../model/PagedData";

interface ITextureFormErrors {
  name: string;
  description: string;
  type: string;
  license_type: string;
  id_gltf_file: string;
  texture_meaning: string;
  texture_file: string;
  new_texture_files: Map<string, string>;
}

const defaultFormErrors: ITextureFormErrors = {
  name: "",
  description: "",
  type: "",
  license_type: "",
  id_gltf_file: "",
  texture_meaning: "",
  texture_file: "",
  new_texture_files: new Map<string, string>(),
};

function TextureAddEdit() {
  // read the IDs from the url
  const { e3dinstid, id } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // manage uploading & creating animations
  const [uploading, setUploading] = useState(false);
  // manage upload progress display
  const [uploadProgress, setUploadProgress] = useState<
    UploadManagerProgressReport | undefined
  >(undefined);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form info
  const [formData, setFormData] = useState<Texture>(Texture.EmptyInstance());
  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<ITextureFormErrors>(defaultFormErrors);

  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // instance of UploadManager
  const updateUploadProgress = (info: UploadManagerProgressReport) => {
    setUploadProgress(info);
  };
  const [referenceTextureFiles, setReferenceTextureFiles] = useState<
    Map<string, TextureFile>
  >(new Map<string, TextureFile>());
  const [newTextureFiles, setNewTextureFiles] = useState<Map<string, File>>(
    new Map<string, File>(),
  );

  const [uploadManager] = useState(
    new UploadManager({
      apiClient: apiClient,
      progressCallback: updateUploadProgress,
    }),
  );

  const [agreeTerms, setAgreeTerms] = useState<boolean>(false);

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let texture: Texture | undefined = undefined;
        if (id) {
          texture = await apiClient?.get<Texture>({
            url: APIEndPoints.TEXTURES.ONE(parseInt(id)),
          });
        }

        // unify initial model with data from all sources
        const currentData = texture ? texture : Texture.EmptyInstance();
        if (e3dinstid) {
          currentData.id_3Dinstance = parseInt(e3dinstid);
        }

        // if this is an empty instance, get the information about the Default texture
        if (!id && e3dinstid) {
          const allTextures = await apiClient?.get<PagedData<Texture[]>>({
            url: APIEndPoints.TEXTURES.ALL(parseInt(e3dinstid)),
          });
          // identify the default texture, based on the fact that it is the only texture
          // that has the GLTF file defined
          const defaultTexture = allTextures.items.filter(
            (t) => t.gltf_name != undefined && t.gltf_name != "",
          );
          // make sure we have at least one texture
          if (defaultTexture.length > 0) {
            // extract the files that will be the references
            const fileReferences = defaultTexture[0].textures?.filter(
              (file) => file.texture_type == TextureFileType.ALBEDO_DIFFUSE,
            );
            // create the data model for the reference files
            let auxReferencesMap = new Map<string, TextureFile>();
            fileReferences?.map((file) => {
              auxReferencesMap.set(file.name ?? "ERROR: no file name", file);
            });
            setReferenceTextureFiles(auxReferencesMap);
          }
        }

        // set the texture meaning information from files to actual texture record
        if (currentData.textures && currentData.textures.length > 0) {
          currentData.texture_meaning = currentData.textures[0].texture_type;
        }

        // set into the state all the information
        setFormData(currentData);
        setFormErrors(defaultFormErrors);
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id, e3dinstid]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // validate errors from the form
    const errors = validateSiteForm(
      formData,
      !!id,
      referenceTextureFiles,
      newTextureFiles,
    );

    if (isFormInvalid(errors)) {
      setFormErrors(errors);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
      return;
    }

    var iconServerId: number | undefined = id ? formData.icon : undefined;
    var gltfServerId: number | undefined = formData.id_gltf_file;
    var gltfTextureFilesServerIds: TextureFile[] = [];
    var uploadErrors = "";
    // we need reference from new files to their reference files for after-upload data setup
    var newTextureFileToReferenceTextureFile = new Map<string, string>();

    // we sync all the uploads
    if (newTextureFiles.size > 0) {
      newTextureFiles.forEach((value: File, key: string) => {
        uploadManager.addFile("textures", UploadFileType.TEXTURE, value);
        // save the cross reference newFile => referenceFile
        newTextureFileToReferenceTextureFile.set(value.name, key);
      });
    }

    // we upload only if it is a CREATE or if is EDIT & we have some files (e.g. icon)
    if (!id || uploadManager.hasAnyFilesPending()) {
      try {
        setUploading(true);
        // upload the files
        uploadManager.resetUploads();
        const uploadResult = await uploadManager.uploadFiles();
        console.log(uploadResult);

        if (uploadResult instanceof Array) {
          uploadResult.forEach((result) => {
            if (result.status !== UploadFileStatus.COMPLETE) {
              uploadErrors += " ERROR uploading file: " + result.fileName;
            } else if (result.identifier === "icon") {
              iconServerId = result.serverId;
            } else if (result.identifier === "gltf_file" && result.serverId) {
              gltfServerId = result.serverId;
            } else if (result.identifier === "textures" && result.serverId) {
              // get the reference file
              const refFileName =
                newTextureFileToReferenceTextureFile.get(result.fileName) ?? "";
              const refFilePointer = referenceTextureFiles.get(refFileName);
              gltfTextureFilesServerIds.push(
                new TextureFile(
                  result.serverId,
                  formData.texture_meaning
                    ? formData.texture_meaning
                    : TextureFileType.ALBEDO_DIFFUSE,
                  undefined,
                  undefined,
                  refFilePointer ? refFilePointer.id : undefined,
                  refFilePointer ? refFilePointer.name : undefined,
                ),
              );
            }
          });
        }
      } catch (exception) {
        uploadErrors += " " + exception;
        console.log(exception);
        uploadErrors += " " + exception;
      }

      if (uploadErrors !== "") {
        alertsManager.showAlertMessage({
          type: AlertMessageType.ERROR,
          message: uploadErrors,
        });
        setUploading(false);
        return;
      }
    }

    var body = new Texture(
      authManager.getUserID()!!,
      formData.id_3Dinstance,
      gltfServerId,
      iconServerId,
      formData.name,
      formData.description,
      formData.sensor_configuration,
      formData.type,
      formData.license_type,
      gltfTextureFilesServerIds ? gltfTextureFilesServerIds : undefined,
      formData.config,
    );

    // check if we have EDIT or CREATE
    if (id) {
      body.id = parseInt(id);
      apiClient
        ?.put({
          url: APIEndPoints.TEXTURES.ONE(parseInt(id)),
          body: body,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors); // Reset errors
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Texture updated successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.TEXTURES,
              id: parseInt(id),
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
          setUploading(false);
        });
    } else {
      apiClient
        ?.post<Texture>({
          url: APIEndPoints.TEXTURES.NEW(body.id_3Dinstance),
          body: body,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors);
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Texture created successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.TEXTURES,
              id: response.id,
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
          setUploading(false);
        });
    }
  };

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.TEXTURES.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Texture deleted successfully",
        });
        navigate(
          SETTINGS.go_back({
            section: AppSections._3DINSTANCES,
            id: formData.id_3Dinstance,
            subsection: AppSubSections.VIEW,
          }),
        );
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete the texture",
          });
        }
      });
  };

  const formInputChange = (inputID: string, newValue: string) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[inputID] = newValue;
    setFormData(formDataCopy as Texture);
  };

  const formIconChange = (
    id: string,
    value: string | undefined,
    files: FileList | null,
  ) => {
    uploadManager.replaceFiles(id, UploadFileType.ICON, files);
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy["icon_path"] = value;
    formDataCopy["icon"] = -1;
    setFormData(formDataCopy as Texture);
  };

  const formFileChange = (
    fieldId: string,
    keyForData: string,
    keyForDisplay: string,
    files: FileList | null,
  ) => {
    let uploadFileType: UploadFileType | undefined = undefined;
    let display = "";

    if (fieldId === "gltf_file") {
      uploadFileType = UploadFileType.TEXTURE_LAYER;
    } else if (fieldId === "textures") {
      uploadFileType = UploadFileType.TEXTURE;
    }
    if (uploadFileType) {
      uploadManager.replaceFiles(fieldId, uploadFileType, files);

      // process the display, based on the name of the files
      if (files) {
        for (let i = 0; i < files.length; i++) {
          display += display === "" ? files[i].name : ", " + files[i].name;
        }
      }
    } else {
      display = "ERROR: Upload file type error! Please contact developer.";
    }
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[keyForDisplay] = display === "" ? undefined : display;
    formDataCopy[keyForData] = -1;
    setFormData(formDataCopy as Texture);
  };

  const newTextureFileChange = (
    fieldId: string,
    keyForData: string,
    keyForDisplay: string,
    files: FileList | null,
  ) => {
    // check if we have a file selected
    if (files === null) return;
    // check if the filename is unique
    var auxFormErrors = JSON.parse(
      JSON.stringify(formErrors),
    ) as ITextureFormErrors;
    // clear the errors related with the files
    auxFormErrors.new_texture_files = new Map<string, string>();
    newTextureFiles.forEach((value, key) => {
      if (
        value.name.toLocaleLowerCase() === files[0].name.toLocaleLowerCase()
      ) {
        auxFormErrors.new_texture_files.set(
          key,
          "To prevent confusion, identical file names are not allowed.",
        );
        auxFormErrors.new_texture_files.set(
          keyForData,
          "To prevent confusion, identical file names are not allowed.",
        );
      }
    });
    if (auxFormErrors.new_texture_files.size > 0) {
      setFormErrors(auxFormErrors);
      return;
    }
    const dataCopy = new Map(newTextureFiles);
    dataCopy.set(keyForData, files[0]);
    setNewTextureFiles(dataCopy);
  };

  const onFormCancel = () => {
    navigate(
      SETTINGS.go_back({
        section: AppSections._3DINSTANCES,
        id: formData.id_3Dinstance ?? parseInt(e3dinstid!),
        subsection: AppSubSections.VIEW,
      }),
    );
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = defaultFormErrors;
      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg;
      });
      setFormErrors(formErrorsCopy);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
    } catch (exception) {
      console.log(exception);
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };

  const textureTypeOptions = Object.entries(TextureType).map(
    ([key, value]) => ({
      label: key.replace(/_/g, " "),
      value: value.toString(),
    }),
  );
  textureTypeOptions.splice(0, 0, {
    value: "",
    label: "-- please select texture type --",
  });

  const textureMeaningOptions = Object.entries(TextureFileType).map(
    ([key, value]) => ({
      label: key.replace(/_/g, " "),
      value: value.toString(),
    }),
  );
  textureMeaningOptions.splice(0, 0, {
    value: "",
    label: "-- please select texture meaning --",
  });

  const licenceTypeOptions = Object.entries(_3DModelLicenseType).map(
    ([key, value]) => ({
      label: key.replace(/_/g, " "),
      value: value.toString(),
    }),
  );
  licenceTypeOptions.splice(0, 0, {
    value: "",
    label: "-- please select licence type --",
  });

  const pageTitle = id ? "Edit texture" : "Create a new texture";
  const confirmLabel = id ? "Update texture" : "Create texture";

  const deleteButton = id ? (
    <Button
      label="Delete texture"
      purpose={ButtonAction.DELETE}
      onClick={() => {
        alertsManager.showDialogWindow({
          type: DialogWindowType.CONFIRM,
          message: "Are you sure you want to delete this texture?",
          cancelLabel: "Cancel",
          confirmLabel: "Yes, delete texture",
          onConfirm: handleDelete,
        });
      }}
    />
  ) : null;

  const filesUploadDisplay: ReactNode[] = [];
  referenceTextureFiles.forEach((value: TextureFile, key) => {
    const auxNewFile = newTextureFiles.get(value.name!);
    filesUploadDisplay.push(
      <>
        <div className="form-upload-texture-file">
          <strong>Replace file:</strong>&nbsp;&nbsp;&nbsp;
          <div
            style={{ width: "210px", whiteSpace: "nowrap", overflow: "clip" }}
          >
            {value.name}
          </div>
          &nbsp;&nbsp;&nbsp;<strong>with the file:</strong>
          <div className="ml-6">
            <FormInputFile
              label=""
              id={"texture_file_" + value.name!}
              keyForData={value.name!}
              value={auxNewFile ? auxNewFile.name : ""}
              required={false}
              disabled={!!id}
              multipleFiles={false}
              acceptedFileTypes=".png, .jpg, .tiff"
              onChange={newTextureFileChange}
              css="overflow-hidden whitespace-nowrap w-[280px]"
            />
          </div>
        </div>
        {formErrors.new_texture_files.get(value.name!) && (
          <div className="form-error" style={{ marginTop: "0px" }}>
            {formErrors.new_texture_files.get(value.name!)}
          </div>
        )}
      </>,
    );
  });

  if (loading || uploading) {
    return (
      <Loading
        uploading={uploading}
        uploadingMessage="Please wait. Uploading texture files..."
        uploadingProgress={uploadProgress}
      />
    );
  }

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={(e) => {
        handleSubmit(e);
      }}
      headButton={deleteButton}
    >
      <FormInputImage
        label="Picture"
        id="icon"
        placeholderImage=""
        value={ImgUtil.display_icon(formData.icon_path)}
        required={false}
        onChange={formIconChange}
      />

      <FormInputText
        label="Name"
        id="name"
        value={formData.name}
        required={true}
        placeholder="Name of texture"
        onChange={formInputChange}
        error={formErrors.name}
        hint="Please provide a human-friendly identifier relevant for this texture. For example 'Created with RGB drone scan'."
      />

      <FormTextArea
        label="Description"
        id="description"
        value={formData.description}
        required={true}
        onChange={formInputChange}
        error={formErrors.description}
        hint="Describe succintly the main points of this texture (source sensor, creation methodology, ...), so you can easilly differentiate between multiple textures added to the same scan."
      />

      <FormTextArea
        label="Sensors configuration"
        id="sensor_configuration"
        value={formData.sensor_configuration}
        required={false}
        onChange={formInputChange}
        hint="Give some details on the sensors used to create this texture. For example: 12mp camera, on iPhone 12 pro"
      />

      <FormDropDown
        label="Texture type"
        id="type"
        value={formData.type}
        required={true}
        options={textureTypeOptions}
        onChange={formInputChange}
        error={formErrors.type}
      />

      <FormDropDown
        label="License type"
        id="license_type"
        value={formData.license_type}
        required={true}
        options={licenceTypeOptions}
        onChange={formInputChange}
        error={formErrors.license_type}
      />

      {/*}
      {!id && false && (
        <FormInputFile
          label="GLTF file"
          id="gltf_file"
          keyForData="id_gltf_file"
          keyForDisplay="gltf_name"
          value={formData.gltf_name}
          required={true}
          disabled={!!id}
          acceptedFileTypes=".gltf"
          onChange={formFileChange}
          error={formErrors.id_gltf_file}
        />
      )}

      {id && false && (
        <FormDownloadFile
          label="GLTF file"
          file_name={formData.gltf_name}
          file_upload_id={formData.id_gltf_file!}
          warning_message="You cannot change this entry. Updated versions should be published as
        new entries."
        />
      )}*/}

      {!id && referenceTextureFiles.size > 0 && (
        <label className="form-label">Image files reference rules *:</label>
      )}

      {!id && referenceTextureFiles.size > 0 && filesUploadDisplay}

      {/* in case we do not find any clear reference files (ALBEDO TEXTURES) we display the default, one file, upload */}
      {!id && referenceTextureFiles.size == 0 && (
        <>
          <FormDropDown
            label="Texture meaning"
            id="texture_meaning"
            value={formData.texture_meaning}
            required={true}
            options={textureMeaningOptions}
            onChange={formInputChange}
            error={formErrors.texture_meaning}
          />

          <FormInputFile
            label="Image file"
            id="textures"
            keyForData="textures"
            keyForDisplay="textures_name"
            value={formData.textures_name}
            required={true}
            disabled={!!id}
            multipleFiles={false}
            acceptedFileTypes=".png, .jpg, .tiff"
            onChange={formFileChange}
            error={formErrors.texture_file}
          />
        </>
      )}

      {id &&
        Array.isArray(formData.textures) &&
        formData.textures.length > 0 && (
          <label className="form-label">Images:</label>
        )}

      {id &&
        Array.isArray(formData.textures) &&
        formData.textures.map((texture) => {
          return (
            <FormDownloadFile
              key={texture.id}
              file_name={texture.name}
              file_upload_id={texture.id}
              warning_message={
                texture.name_reference
                  ? "This file replaces " +
                    texture.name_reference +
                    " from the default texture"
                  : ""
              }
            />
          );
        })}

      {errorMessage && (
        <div className="form-submit-error">
          <FaInfoCircle />
          <div>{errorMessage}</div>
        </div>
      )}

      {/* License Agreement group */}
      <div className="form-input-group mt-4 flex flex-col gap-2 pl-4">
        <label className="text-sm">{EXPLANATION_HEADER}</label>

        <label className="flex items-center gap-2 text-sm mt-1">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <span>
            I agree with the{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alertsManager.showDialogWindow({
                  type: DialogWindowType.LICENSE,
                  message: "license",
                });
              }}
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Terms and Conditions
            </a>
          </span>
        </label>
      </div>

      <div className="form-buttons-container">
        <Button
          label="Cancel"
          purpose={ButtonAction.CANCEL}
          onClick={() => onFormCancel()}
          className="w-full"
        />
        <Button
          label={confirmLabel}
          purpose={ButtonAction.SAVE}
          isSubmit={true}
          disabled={!agreeTerms}
          className="w-full"
        />
      </div>
    </FormContainer>
  );
}

function validateSiteForm(
  formData: Texture,
  isEdit: boolean,
  referenceTextureFiles: Map<string, TextureFile>,
  newTextureFiles: Map<string, File>,
): ITextureFormErrors {
  let errors: ITextureFormErrors = {
    name: "",
    description: "",
    type: "",
    license_type: "",
    id_gltf_file: "",
    texture_meaning: "",
    texture_file: "",
    new_texture_files: new Map<string, string>(),
  };

  errors.name =
    formData.name.length < 4
      ? "Texture name should be at least 4 characters long"
      : "";
  errors.description =
    formData.description.length < 10
      ? "Texture description should be at least 10 characters long"
      : "";
  errors.type = !formData.type ? "Please select the type of this texture" : "";
  errors.license_type = !formData.license_type
    ? "Please select the license type for this texture"
    : "";
  errors.texture_meaning =
    !formData.texture_meaning && referenceTextureFiles.size == 0
      ? "Please indicate the meaning of this texture"
      : "";
  if (!isEdit) {
    errors.texture_file =
      !formData.textures_name && referenceTextureFiles.size == 0
        ? "Please select an image file to upload"
        : "";

    // for multiple textures, make sure each of them has a correspondance
    if (referenceTextureFiles.size > 0) {
      Array.from(referenceTextureFiles.keys()).map((key: string) => {
        if (!newTextureFiles.get(key)) {
          errors.new_texture_files.set(
            key,
            "Please upload a file for this texture",
          );
        }
      });
    }
  }

  return errors;
}

function isFormInvalid(errors: ITextureFormErrors): boolean {
  return (
    errors.name !== "" ||
    errors.description !== "" ||
    errors.type !== "" ||
    errors.license_type !== "" ||
    errors.texture_meaning !== "" ||
    errors.texture_file !== "" ||
    errors.new_texture_files.size > 0
  );
}

export default TextureAddEdit;
